/* global faceapi */
/**
 * silentFaceCapture
 *
 * Opens the front-facing camera invisibly (no on-screen preview), waits for
 * the first stable frame, runs face-api detection, returns the 128-float face
 * descriptor, and tears down the stream. Designed to be invoked from inside a
 * user-gesture handler (e.g. the "Check in" button click) so mobile browsers
 * will surface their one-time permission prompt without blocking.
 *
 * Throws:
 *   - "Camera permission denied" (NotAllowedError / SecurityError)
 *   - "No camera found" (NotFoundError)
 *   - "Camera in use" (NotReadableError)
 *   - "No face detected" (face-api couldn't find a face in `timeoutMs`)
 *   - "Camera timeout" (stream never produced frames)
 */

import { ensureModels } from "@/lib/faceModels";

function friendlyError(e) {
    const name = e?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") return "Camera permission denied. Allow camera access in your browser settings.";
    if (name === "NotFoundError" || name === "OverconstrainedError") return "No front-facing camera found on this device.";
    if (name === "NotReadableError") return "Your camera is already in use by another app.";
    if (name === "AbortError") return "Camera was interrupted. Please try again.";
    return e?.message || "Could not open the camera.";
}

/**
 * @param {object} opts
 * @param {number} [opts.timeoutMs=8000]  — overall budget for the capture
 * @param {number} [opts.maxAttempts=6]   — how many detection attempts before giving up
 * @returns {Promise<number[]>} 128-float face descriptor
 */
export async function silentFaceCapture(opts = {}) {
    const timeoutMs = opts.timeoutMs || 8000;
    const maxAttempts = opts.maxAttempts || 6;
    // Kick off models in parallel with the permission prompt.
    const modelsPromise = ensureModels();

    let stream = null;
    let videoEl = null;
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: {
                facingMode: { ideal: "user" },
                width: { ideal: 640 },
                height: { ideal: 480 },
            },
            audio: false,
        });

        // Off-screen, invisible <video> element. We append to the DOM so iOS
        // Safari will actually start producing frames (some versions refuse to
        // play streams attached to detached/hidden-display elements).
        videoEl = document.createElement("video");
        videoEl.muted = true;
        videoEl.autoplay = true;
        videoEl.playsInline = true;
        videoEl.setAttribute("aria-hidden", "true");
        Object.assign(videoEl.style, {
            position: "fixed",
            left: "-9999px",
            top: "0",
            width: "1px",
            height: "1px",
            opacity: "0",
            pointerEvents: "none",
        });
        document.body.appendChild(videoEl);
        videoEl.srcObject = stream;

        // iOS requires the play() inside a gesture — caller should have triggered us
        // from a button click, so this is allowed.
        try { await videoEl.play(); } catch (_) { /* ignore */ }

        // Wait for the stream to actually deliver a frame.
        const playStart = Date.now();
        while (videoEl.readyState < 2 || videoEl.videoWidth === 0) {
            if (Date.now() - playStart > 3000) throw new Error("Camera timeout");
            await new Promise((r) => setTimeout(r, 100));
        }

        await modelsPromise;

        // Try detection up to maxAttempts times with small delays — gives the
        // user a moment to face the camera and lets autoexposure settle.
        const overallStart = Date.now();
        const detectorOpts = new window.faceapi.TinyFaceDetectorOptions({
            inputSize: 320,
            scoreThreshold: 0.5,
        });
        let lastErr = null;
        for (let i = 0; i < maxAttempts; i++) {
            if (Date.now() - overallStart > timeoutMs) break;
            try {
                const res = await window.faceapi
                    .detectSingleFace(videoEl, detectorOpts)
                    .withFaceLandmarks()
                    .withFaceDescriptor();
                if (res && res.descriptor) {
                    return Array.from(res.descriptor);
                }
            } catch (e) {
                lastErr = e;
            }
            await new Promise((r) => setTimeout(r, 250));
        }
        throw new Error(lastErr?.message || "No face detected. Make sure your face is clearly visible to the camera.");
    } catch (e) {
        if (e && (e.name || "").length) throw new Error(friendlyError(e));
        throw e;
    } finally {
        try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        try { if (videoEl) videoEl.remove(); } catch (_) { /* ignore */ }
    }
}

/**
 * Warm-load the face-api models in the background. Safe to call multiple times.
 * Lets us start downloading models the moment the page mounts so the eventual
 * silent capture is faster.
 */
export function prewarmFaceModels() {
    // Fire-and-forget; ignore errors here.
    ensureModels().catch(() => {});
}
